/* eslint-disable @typescript-eslint/explicit-function-return-type */
import type { PostgrestSingleResponse } from "@supabase/postgrest-js";
import type { Database } from "../../../__definitions__/database-definitions";
import {
  type GetAllOptions,
  SupabaseService,
  SupabaseServiceView,
} from "../../../server/supabase/base-supabase-service";
import { toISO8601 } from "../../../utils/iso8601";
import { type UUID } from "../../../utils/uuid";
import type { ChannelStatus } from "../../channels/types";
import type { Segment } from "../types";

type BaseSegmentData = Database["public"]["Views"]["segment_data_v2"]["Row"];

type SegmentData = NonNullableFields<BaseSegmentData, "id" | "name" | "is_recipient_type">;

export type UsedSegmentInfo = Database["public"]["Views"]["used_segments_info"]["Row"];

export type SupabaseSegment = Database["public"]["Tables"]["segment"]["Row"];

export type SupabaseSegmentWithFavourite = NonNullable<
  Awaited<ReturnType<SupabaseSegmentService["getAllWithFavoriteSegment"]>>["data"]
>[number];

export type PersonInUnsavedSegmentWithChannels =
  Database["public"]["Functions"]["get_persons_in_segment_json"]["Returns"][number] & {
    channels: {
      slack?: ChannelStatus;
      teams?: ChannelStatus;
      work_email?: ChannelStatus;
      personal_email?: ChannelStatus;
      twilio?: ChannelStatus;
      whatsapp?: ChannelStatus;
    };
  };

export class SupabaseUsedSegmentInfoService extends SupabaseServiceView<"used_segments_info"> {
  table = "used_segments_info" as const;
}

export class SupabaseSegmentService extends SupabaseService<"segment"> {
  table = "segment" as const;

  async segmentSaveAllowed() {
    return this.client.rpc("segment_save_allowed");
  }

  async getAllWithFavoriteSegment(
    account_id: UUID,
    options: GetAllOptions<Database["public"]["Tables"]["segment"]["Row"]>,
  ) {
    return await this.getAllFromSelection(
      account_id,
      this.client
        .from(this.table)
        .select("id, name, description, deleted_at, favorite_segment(id)"),
      options,
    );
  }

  async getCount(
    segment_id: UUID,
    account_id: UUID,
    target_manager: boolean,
    target_connection_type?: string | null,
  ) {
    const args: {
      segment_id_: UUID;
      account_id_: UUID;
      target_manager_: boolean;
      target_connection_type_?: string;
    } = {
      segment_id_: segment_id,
      account_id_: account_id,
      target_manager_: target_manager,
    };

    if (target_connection_type) {
      args.target_connection_type_ = target_connection_type;
    }

    return this.getAllFromSelection(null, this.client.rpc("get_persons_in_segment_json", args), {
      order: [],
    });
  }

  async getAllUnsavedRecipients(
    account_id: UUID,
    segment_json: Segment["segment_json"],
    target_connection_type_?: string,
  ) {
    return this.log_errors(
      await this.getAllFromSelection(
        account_id,
        this.client.rpc("get_persons_in_segment_json", {
          account_id_: account_id,
          segment_json,
          target_connection_type: target_connection_type_ || undefined,
        }),
        {
          order: [{ column: "email", dir: "asc" }],
        },
      ),
    );
  }

  async getSegmentOnly(segment_id: UUID) {
    return this.log_errors(
      await this.client.from(this.table).select().eq("id", segment_id).single(),
    );
  }

  async getMomentIds(segment_id: UUID) {
    return this.log_errors(
      await this.client.from("moment").select("id").eq("segment_id", segment_id),
    );
  }

  async getDefaultSegment() {
    return this.log_errors(
      await this.client.from("segment_data_v2").select("*").eq("is_default", true).maybeSingle(),
    );
  }

  cloneSegment(segment: SegmentData, newId: UUID) {
    return {
      ...segment,
      id: newId,
      name: "",
      conditions: null,
      created_at: toISO8601(new Date()),
      deleted_at: null,
      is_default: false,
      operation: segment.operation !== null ? segment.operation : "AND",
      is_recipient_type: segment.is_recipient_type !== null ? segment.is_recipient_type : false,
    };
  }

  async getSegmentData(segment_id: UUID) {
    return this.log_errors(
      await this.client.from("segment_data_v2").select("*").eq("id", segment_id).single(),
    ) as PostgrestSingleResponse<SegmentData>;
  }

  async upsertSegmentData(segment: Segment) {
    return this.log_errors(
      await this.client.from(this.table).upsert({
        id: segment.id,
        name: segment.name ?? "",
        description: segment.description ?? "",
        is_recipient_type: segment.is_recipient_type,
        segment_json: segment.segment_json,
      }),
    );
  }

  async archive(segment_id: UUID) {
    return this.log_errors(
      await this.client
        .from(this.table)
        .update({ deleted_at: toISO8601(new Date()) })
        .eq("id", segment_id),
    );
  }

  async unarchive(segment_id: UUID) {
    return this.log_errors(
      await this.client.from(this.table).update({ deleted_at: null }).eq("id", segment_id),
    );
  }

  async unarchiveWithNewName(segment_id: UUID, name: string) {
    return this.log_errors(
      await this.client
        .from(this.table)
        .update({ deleted_at: null, name: name })
        .eq("id", segment_id),
    );
  }
}

export type SegmentWithMoments =
  Database["public"]["Views"]["segments_with_related_moments"]["Row"];

export class SupabaseSegmentsWithMomentsService extends SupabaseServiceView<"segments_with_related_moments"> {
  table = "segments_with_related_moments" as const;
}

export class SupabaseFavoriteSegmentService extends SupabaseService<"favorite_segment"> {
  table = "favorite_segment" as const;

  async unfavorite(accountId: UUID, segmentId: UUID) {
    return await this.client
      .from(this.table)
      .delete()
      .eq("account_id", accountId)
      .eq("segment_id", segmentId);
  }
}

type NonNullableAllFields<T> = {
  [P in keyof T]: NonNullable<T[P]>;
};

type NonNullableFields<T, P extends keyof T> = Omit<T, P> & NonNullableAllFields<Pick<T, P>>;
